import { Timestamp } from "@bufbuild/protobuf";
import { ConversationsChatMessage } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/conversations_pb";

import type {
  ConversationMessage,
  ConversationMessageJsonContent,
  MessageStatus,
} from "@mirage/service-conversation/types";

/**
 * Safely converts a timestamp from various possible formats to a Date object
 * @param timestamp The timestamp to convert
 * @returns A Date object or undefined if conversion is not possible
 */
const safelyConvertTimestamp = (timestamp?: Timestamp): Date | undefined => {
  if (!timestamp) return undefined;

  if (
    typeof timestamp === "object" &&
    "seconds" in timestamp &&
    "nanos" in timestamp &&
    typeof timestamp.seconds === "bigint"
  ) {
    // Convert BigInt to number (safe as timestamps are within Number.MAX_SAFE_INTEGER)
    const milliseconds =
      Number(timestamp.seconds) * 1000 +
      Math.floor(Number(timestamp.nanos) / 1e6);
    return new Date(milliseconds);
  }

  return undefined;
};

/**
 * Converts a ConversationsChatMessage from the gRPC API to our internal ConversationMessage type
 * @param message The ConversationsChatMessage to convert
 * @param status The status to assign to the converted message
 * @returns A ConversationMessage
 */
export const convertToConversationMessage = (
  message: ConversationsChatMessage,
  status: MessageStatus,
): ConversationMessage => {
  // Safely convert timestamp if it exists
  const timestamp = safelyConvertTimestamp(message.timestamp);
  let jsonContent;
  switch (message.messageType.case) {
    case "systemMessage":
      return {
        id: message.id,
        timestamp,
        messageType: message.messageType,
        status,
      };
    case "assistantMessage":
      // Clean up the transport variable from the updated type and set it
      // on the message
      if ("_jsonContent" in message) {
        jsonContent = {
          ...(message._jsonContent as ConversationMessageJsonContent),
        };
        // TODO (Will) - removed this for now since it causes flashing and seems ok without
        // and is causingsome weird javascript issue where the object reference is getting lost
        // delete message._jsonContent;
      }
      return {
        id: message.id,
        timestamp,
        messageType: {
          case: "assistantMessage",
          value: {
            text: message.messageType.value.text,
            isCached: message.messageType.value.isCached,
            cited: message.messageType.value.cited.map((cited) => ({
              ...cited,
              lastUpdatedMs: Number(cited.lastUpdatedMs),
            })),
            context: message.messageType.value.context.map((context) => ({
              ...context,
              lastUpdatedMs: Number(context.lastUpdatedMs),
            })),
            agentId: message.messageType.value.agentId,
            format: message.messageType.value.format,
          },
        },
        status,
        jsonContent,
      };
    case "userMessage":
      return {
        id: message.id,
        messageType: {
          case: "userMessage",
          value: {
            ...message.messageType.value,
            agentPayload: message.messageType.value.agentPayload,
            llmIgnore: message.messageType.value.llmIgnore,
            attachments: message.messageType.value.attachments.map(
              (attachment) => ({
                ...attachment,
                lastUpdatedMs: Number(attachment.lastUpdatedMs),
              }),
            ),
          },
        },
        timestamp,
        status,
      };
    default:
      return {
        id: message.id,
        timestamp: timestamp,
        messageType: { case: undefined },
        status: status,
      };
  }
};

/**
 * Converts our internal ConversationMessage type to a ConversationsChatMessage for the gRPC API
 * @param message The ConversationMessage to convert
 * @returns A ConversationsChatMessage
 */
export const convertToConversationsChatMessage = (
  message: ConversationMessage,
): ConversationsChatMessage => {
  const grpcTimestamp = message.timestamp
    ? new Timestamp({
        seconds: BigInt(Math.floor(message.timestamp.getTime() / 1000)),
        nanos: (message.timestamp.getTime() % 1000) * 1e6,
      })
    : undefined;
  switch (message.messageType.case) {
    case "systemMessage":
      return new ConversationsChatMessage({
        id: message.id,
        timestamp: grpcTimestamp,
        messageType: {
          case: message.messageType.case,
          value: message.messageType.value,
        },
      });
    case "assistantMessage":
      return new ConversationsChatMessage({
        id: message.id,
        timestamp: grpcTimestamp,
        messageType: {
          case: message.messageType.case,
          value: {
            text: message.messageType.value.text,
            isCached: message.messageType.value.isCached,
            cited: message.messageType.value.cited.map((cited) => ({
              ...cited,
              lastUpdatedMs: BigInt(cited.lastUpdatedMs),
            })),
            context: [],
            agentId: message.messageType.value.agentId,
            format: message.messageType.value.format,
          },
        },
      });
    case "userMessage":
      return new ConversationsChatMessage({
        id: message.id,
        timestamp: grpcTimestamp,
        messageType: {
          case: message.messageType.case,
          value: {
            ...message.messageType.value,
            attachments: message.messageType.value.attachments.map(
              (attachment) => ({
                ...attachment,
                lastUpdatedMs: BigInt(attachment.lastUpdatedMs),
              }),
            ),
            agentPayload: message.messageType.value.agentPayload,
          },
        },
      });
    default:
      return new ConversationsChatMessage({
        id: message.id,
        timestamp: grpcTimestamp,
      });
  }
};
